import { useEffect, useRef, useState } from "react";
import { NotificationManager } from "react-notifications";
import { SobranteReclasificacionApi } from "../api/SobranteReclasificacionApi";
import { getErrorFromResponse } from "../../../../../../../../../utility/utils";
import { PESO_KG_PACA_PRODUCCION } from "../../../../../../../../../utility/constants";

// agrupa el sobrante disponible (métricas sin paletizar) por padre PT y
// devuelve las pacas COMPLETAS disponibles por padre. El sobrante de un
// producto sirve para cualquier producto del mismo padre, así que se suma
// por padre; una fracción que no completa una paca no se puede paletizar y
// no se cuenta. Se suma en gramos enteros para evitar error de punto flotante
export const pacasCompletasSobrantePorPadre = (sobrantes) => {
    const gramosPorPadre = {};

    (sobrantes || []).forEach(function (sobrante) {
        const padreId = sobrante.padre_producto_terminado_id;

        gramosPorPadre[padreId] =
            (gramosPorPadre[padreId] || 0) +
            Math.round(Number(sobrante.peso || 0) * 1000);
    });

    const pacasPorPadre = {};

    Object.keys(gramosPorPadre).forEach(function (padreId) {
        pacasPorPadre[padreId] = Math.floor(
            gramosPorPadre[padreId] / (PESO_KG_PACA_PRODUCCION * 1000),
        );
    });

    return pacasPorPadre;
};

// se trabaja en gramos enteros para evitar errores de punto flotante al
// validar que los kg completen pacas enteras
export const calcularTotalesSobrante = (existencias) => {
    const seleccionados = (existencias || []).filter(function (existencia) {
        return parseFloat(existencia.kg_a_consumir) > 0;
    });

    const totalGramos = seleccionados.reduce(function (total, existencia) {
        return total + Math.round(parseFloat(existencia.kg_a_consumir) * 1000);
    }, 0);

    const totalKg = totalGramos / 1000;

    return {
        seleccionados,
        totalKg,
        pacas: totalKg / PESO_KG_PACA_PRODUCCION,
        esMultiploDePaca:
            totalGramos > 0 &&
            totalGramos % (PESO_KG_PACA_PRODUCCION * 1000) === 0,
    };
};

export const useSobranteReclasificacion = ({
    onClose,
    sobrantesReclasificacion,
    setSobrantesReclasificacion,
    productoId,
    refreshKey,
    sobrantePendiente,
}) => {
    const { getSobrantesReclasificacion, cargando } =
        SobranteReclasificacionApi();

    // guard para descartar respuestas de peticiones viejas cuando el
    // producto cambia rápido
    const requestIdRef = useRef(0);

    const GetSobrantesReclasificacion = async () => {
        const requestId = ++requestIdRef.current;

        // limpiar de inmediato el estado del producto anterior
        setSobrantesReclasificacion([]);

        if (!productoId || !sobrantePendiente) {
            return;
        }

        try {
            const params = {
                producto_id: productoId,
                filtrar_disponibles: true,
                no_paginar: true,
            };

            const response = await getSobrantesReclasificacion(params);

            if (requestId !== requestIdRef.current) {
                return;
            }

            setSobrantesReclasificacion(response || []);
        } catch (error) {
            if (requestId !== requestIdRef.current) {
                return;
            }

            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener el sobrante en reclasificación",
            );
            NotificationManager.error(errorMsg, "Error", 3000);

            setSobrantesReclasificacion([]);
        }
    };

    useEffect(() => {
        GetSobrantesReclasificacion();
    }, [refreshKey, productoId, sobrantePendiente]);

    const [draftExistencias, setDraftExistencias] = useState(
        sobrantesReclasificacion,
    );

    useEffect(() => {
        setDraftExistencias(sobrantesReclasificacion);
    }, [sobrantesReclasificacion]);

    const handleChange = (existenciaId, value) => {
        setDraftExistencias((prev) =>
            prev.map((item) =>
                item.id === existenciaId
                    ? { ...item, kg_a_consumir: value, _dirty: true }
                    : item,
            ),
        );
    };

    const totales = calcularTotalesSobrante(draftExistencias);

    const handleSave = () => {
        if (totales.totalKg > 0 && !totales.esMultiploDePaca) {
            NotificationManager.error(
                `Los kg de sobrante deben completar pacas enteras de ${PESO_KG_PACA_PRODUCCION} kg`,
                "Error",
                3000,
            );
            return;
        }

        setSobrantesReclasificacion(draftExistencias);
        onClose();
    };

    const handleClose = () => {
        // Si hay cambios sin guardar, descartarlos y cerrar
        setDraftExistencias(sobrantesReclasificacion);
        onClose();
    };

    return {
        cargando,

        draftExistencias,
        totales,

        handleChange,
        handleSave,
        handleClose,
    };
};
